import {
  findGroupSizeTier,
  groupSizeLabel as tierLabel,
  toNumber,
  type IGroupSizeRow,
} from '../inventory/activity-pricing.utils';
import { isChargedPerPerson } from './booking-price-variant';

/**
 * Age group and group size pricing for a booked activity.
 *
 * The product carries age groups (a signed euro amount per person) and group
 * size tiers (a signed amount picked by the total headcount), both on top of
 * the chosen duration's price. The FE works out every price; the backend checks
 * the structure, stores the prices as sent and computes the totals from them.
 *
 *   base      = duration price + age group amount + group size amount
 *   unitPrice = max(0, combi(smart(base))), rounded to cents
 *
 * Per person with age groups, each line gets its own `unitPrice` and the
 * activity `price` stays the raw duration price (reference only). Per unit, the
 * first line's amount sets the one `price`. Without age groups, `price` is the
 * tier price, or the duration price when there are no tiers either.
 */

interface IProductAgeGroup {
  id?: string;
  label?: string;
  amount?: number | string | null;
}

/** One age group line as the booking payload sends it. */
interface IBookingAgeGroupLine {
  modifierId: string;
  participantCount: number;
  /** Per person: the final price per person. Per unit: left out. */
  unitPrice?: number;
}

/** One age group line as the form holds it: every product group, 0 allowed. */
interface IAgeGroupCount {
  modifierId?: string | null;
  label?: string | null;
  participantCount?: number | string | null;
}

interface IActivityPricingInput {
  /** The chosen duration's list price (`basePrice` on the form). */
  durationPrice: number;
  priceVariant?: string | null;
  productAgeGroups?: IProductAgeGroup[] | null;
  groupSizes?: IGroupSizeRow[] | null;
  /** The headcount split; ignored when the product has no age groups. */
  ageGroupCounts?: IAgeGroupCount[] | null;
  /** The headcount when the product has no age groups. */
  participantCount: number;
  /** The slot's smart pricing, then the combi deal, on a base price. */
  adjust?: (base: number) => number;
}

interface IActivityPricing {
  hasAgeGroups: boolean;
  /** The activity `price` the payload sends. */
  price: number;
  participantCount: number;
  /** Lines with participants, in the product's order; empty without groups. */
  ageGroups: (IBookingAgeGroupLine & { label: string })[];
  /** What the activity costs, as the backend will store it. */
  totalPrice: number;
  groupSizeLabel: string | null;
}

/** Money rounded half up to cents (EPSILON keeps 1.005 from landing on 1.00). */
const roundMoney = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const countOf = (value: unknown) =>
  Math.max(0, Math.floor(toNumber(value) ?? 0));

/**
 * The product's age groups, in its order, merged with the counts the form holds.
 * A product group missing from the form counts as 0; a form line whose group the
 * product no longer has is dropped.
 */
const mergeAgeGroupCounts = (
  productAgeGroups: IProductAgeGroup[] | null | undefined,
  counts: IAgeGroupCount[] | null | undefined
): { modifierId: string; label: string; amount: number; count: number }[] =>
  (productAgeGroups ?? [])
    .filter((group): group is IProductAgeGroup & { id: string } => !!group.id)
    .map(group => ({
      modifierId: group.id,
      label: group.label ?? '',
      amount: toNumber(group.amount) ?? 0,
      count: countOf(
        counts?.find(line => line.modifierId === group.id)?.participantCount
      ),
    }));

/** The total headcount of a split. */
const sumAgeGroupCounts = (counts: IAgeGroupCount[] | null | undefined) =>
  (counts ?? []).reduce((sum, line) => sum + countOf(line.participantCount), 0);

const priceActivity = ({
  durationPrice,
  priceVariant,
  productAgeGroups,
  groupSizes,
  ageGroupCounts,
  participantCount,
  adjust = base => base,
}: IActivityPricingInput): IActivityPricing => {
  const perPerson = isChargedPerPerson(priceVariant ?? 'per person');
  const merged = mergeAgeGroupCounts(productAgeGroups, ageGroupCounts);
  const hasAgeGroups = merged.length > 0;
  const withPeople = merged.filter(line => line.count > 0);
  const headcount = hasAgeGroups
    ? withPeople.reduce((sum, line) => sum + line.count, 0)
    : countOf(participantCount);

  const tier = findGroupSizeTier(groupSizes ?? [], headcount);
  const tierAmount = toNumber(tier?.amount) ?? 0;
  const finalPrice = (amount: number) =>
    roundMoney(Math.max(0, adjust(durationPrice + amount + tierAmount)));

  const groupSizeLabel = tier
    ? tier.label || tierLabel(tier.minParticipant, tier.maxParticipant)
    : null;

  if (hasAgeGroups && perPerson) {
    const ageGroups = withPeople.map(line => ({
      modifierId: line.modifierId,
      label: line.label,
      participantCount: line.count,
      unitPrice: finalPrice(line.amount),
    }));
    const totalPrice = roundMoney(
      ageGroups.reduce(
        (sum, line) => sum + roundMoney(line.unitPrice * line.participantCount),
        0
      )
    );
    return {
      hasAgeGroups,
      price: durationPrice,
      participantCount: headcount,
      ageGroups,
      totalPrice,
      groupSizeLabel,
    };
  }

  if (hasAgeGroups) {
    const price = finalPrice(withPeople[0]?.amount ?? 0);
    return {
      hasAgeGroups,
      price,
      participantCount: headcount,
      ageGroups: withPeople.map(line => ({
        modifierId: line.modifierId,
        label: line.label,
        participantCount: line.count,
      })),
      totalPrice: price,
      groupSizeLabel,
    };
  }

  const price = finalPrice(0);
  return {
    hasAgeGroups,
    price,
    participantCount: headcount,
    ageGroups: [],
    totalPrice: perPerson ? roundMoney(price * headcount) : price,
    groupSizeLabel,
  };
};

/** The payload's `ageGroups`: lines with people, `label` stripped. */
const toAgeGroupPayload = (pricing: IActivityPricing): IBookingAgeGroupLine[] =>
  pricing.ageGroups.map(({ modifierId, participantCount, unitPrice }) =>
    unitPrice === undefined
      ? { modifierId, participantCount }
      : { modifierId, participantCount, unitPrice }
  );

/** A stored booking activity's age group line, as the API returns it. */
interface IStoredAgeGroupLine {
  id?: string | null;
  modifierId?: string | null;
  label?: string | null;
  participantCount?: number | string | null;
  unitPrice?: number | string | null;
  totalPrice?: number | string | null;
}

/**
 * What a stored activity costs. `totalPrice` from the backend wins; an old row
 * the migration did not fill falls back to today's formula.
 */
const storedActivityTotal = (activity: {
  totalPrice?: number | string | null;
  price?: number | string | null;
  participantCount?: number | string | null;
  priceVariant?: string | null;
}): number => {
  const stored = toNumber(activity.totalPrice);
  if (stored !== undefined) return stored;
  const price = toNumber(activity.price) ?? 0;
  return isChargedPerPerson(activity.priceVariant ?? 'per person')
    ? roundMoney(price * countOf(activity.participantCount))
    : price;
};

/**
 * The default price of an activity line: the duration's list rate, per person
 * times the headcount or once per booking, before age group and group size
 * amounts, smart pricing and combi deals. Undefined without a list rate. A
 * booking shows it struck beside the charged amount whenever the two differ
 * (see `differsFromDefault`), so a tier that lowers or raises the price is
 * visible as such.
 */
const defaultActivityTotal = (activity: {
  basePrice?: number | string | null;
  participantCount?: number | string | null;
  priceVariant?: string | null;
}): number | undefined => {
  const base = toNumber(activity.basePrice);
  if (!base || base <= 0) return undefined;
  return isChargedPerPerson(activity.priceVariant ?? 'per person')
    ? roundMoney(base * countOf(activity.participantCount))
    : base;
};

/** Whether a charged amount differs from its default by at least a cent. */
const differsFromDefault = (
  charged: number,
  defaultTotal: number | undefined
): defaultTotal is number =>
  defaultTotal !== undefined && Math.abs(charged - defaultTotal) >= 0.005;

/**
 * Stored lines, sorted by the product's age group order when the product is
 * known (matched on `modifierId`); lines the product no longer has keep their
 * saved order at the end.
 */
const sortStoredAgeGroups = <T extends IStoredAgeGroupLine>(
  lines: T[] | null | undefined,
  productAgeGroups?: IProductAgeGroup[] | null
): T[] => {
  const order = new Map(
    (productAgeGroups ?? []).map((group, index) => [group.id, index])
  );
  return [...(lines ?? [])].sort(
    (a, b) =>
      (order.get(a.modifierId ?? undefined) ?? Infinity) -
      (order.get(b.modifierId ?? undefined) ?? Infinity)
  );
};

/** The parts of a form activity the pricing below reads. */
interface IPricedFormActivity {
  id?: string | null;
  productId?: string | null;
  price?: number | string | null;
  basePrice?: number | string | null;
  priceVariant?: string | null;
  participantCount?: number | string | null;
  duration?: number | string | null;
  startTime?: string | null;
  totalPrice?: number | string | null;
  /** The group size tier that applies, e.g. "5–10 participants". */
  groupSizeLabel?: string | null;
  ageGroups?: IStoredAgeGroupLine[] | null;
}

/** Lines that hold people, the way a booking stores them. */
const linesWithPeople = (lines: IStoredAgeGroupLine[] | null | undefined) =>
  (lines ?? []).filter(line => countOf(line.participantCount) > 0);

/**
 * Whether an activity's money comes from age group or group size pricing rather
 * than `price × participantCount`: it has age group lines, or a total that the
 * old formula does not explain (a group size tier). Such a line has no single
 * unit price to strike through or quote.
 */
const hasTieredPricing = (activity: IPricedFormActivity): boolean => {
  if (linesWithPeople(activity.ageGroups).length > 0) return true;
  const stored = toNumber(activity.totalPrice);
  if (stored === undefined) return false;
  return (
    Math.abs(stored - storedActivityTotal({ ...activity, totalPrice: null })) >
    0.005
  );
};

/**
 * Everything that moves an activity's price: product, headcount and its split,
 * duration, start time and the day. In edit mode an activity whose signature
 * still matches the saved one keeps its stored lines and prices.
 */
const activityPricingSignature = (
  activity: IPricedFormActivity,
  bookingDate?: string | null
): string =>
  JSON.stringify([
    activity.productId ?? '',
    countOf(activity.participantCount),
    toNumber(activity.duration) ?? 0,
    (activity.startTime ?? '').slice(0, 5),
    bookingDate ?? '',
    linesWithPeople(activity.ageGroups)
      .map(line => [line.modifierId ?? '', countOf(line.participantCount)])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  ]);

/**
 * The form lines for a product: every age group in its order (0 allowed), with
 * the counts already held. With no count anywhere yet (a new item, or a product
 * just swapped in) the whole headcount starts on the first group. Lines whose
 * group the product no longer has stay at the end, so a saved booking's
 * headcount still adds up.
 */
const alignAgeGroupLines = (
  productAgeGroups: IProductAgeGroup[] | null | undefined,
  lines: IStoredAgeGroupLine[] | null | undefined,
  headcount: number
): IStoredAgeGroupLine[] => {
  const merged = mergeAgeGroupCounts(productAgeGroups, lines);
  if (merged.length === 0) return [];
  const known = new Set(merged.map(line => line.modifierId));
  const extra = linesWithPeople(lines).filter(
    line => !known.has(line.modifierId ?? '')
  );
  const empty = merged.every(line => line.count === 0) && extra.length === 0;

  return [
    ...merged.map((line, index) => {
      const held = lines?.find(row => row.modifierId === line.modifierId);
      return {
        ...held,
        modifierId: line.modifierId,
        label: line.label,
        participantCount:
          empty && index === 0 ? Math.max(1, countOf(headcount)) : line.count,
      };
    }),
    ...extra,
  ];
};

/**
 * An activity's line amount: its `totalPrice` when age group or group size
 * pricing set it, else `unitPrice` times the headcount (per person) or once.
 * `unitPrice` lets a caller substitute the price it charges (a combi deal).
 */
const activityAmount = (
  activity: IPricedFormActivity,
  unitPrice: number
): number => {
  if (hasTieredPricing(activity)) return storedActivityTotal(activity);
  return isChargedPerPerson(activity.priceVariant ?? 'per person')
    ? unitPrice * countOf(activity.participantCount)
    : unitPrice;
};

/**
 * The lines for a product, made to add up to `headcount`. The headcount wins
 * because it is also set without the split (the group size question): the first group absorbs a rise, and a drop is taken from
 * the first group onwards. Lines whose group the product no longer has are
 * left alone. The item dialog and the pricing hook both call this, so they
 * always land on the same split.
 */
const reconcileAgeGroupLines = (
  productAgeGroups: IProductAgeGroup[] | null | undefined,
  lines: IStoredAgeGroupLine[] | null | undefined,
  headcount: number
): IStoredAgeGroupLine[] => {
  const aligned = alignAgeGroupLines(productAgeGroups, lines, headcount);
  const known = mergeAgeGroupCounts(productAgeGroups, []).length;
  if (known === 0) return aligned;

  let diff = countOf(headcount) - sumAgeGroupCounts(aligned);
  if (diff === 0) return aligned;

  const counts = aligned.map(line => countOf(line.participantCount));
  if (diff > 0) {
    counts[0] += diff;
  } else {
    for (let i = 0; i < known && diff < 0; i += 1) {
      const take = Math.min(counts[i], -diff);
      counts[i] -= take;
      diff += take;
    }
  }
  return aligned.map((line, index) => ({
    ...line,
    participantCount: counts[index],
  }));
};

/** "2 Adult · 1 Child", or undefined when no line holds people. */
const ageGroupSummary = (
  lines: IStoredAgeGroupLine[] | null | undefined
): string | undefined => {
  const withPeople = linesWithPeople(lines);
  if (withPeople.length === 0) return undefined;
  return withPeople
    .map(line => `${line.participantCount} ${line.label ?? ''}`.trim())
    .join(' · ');
};

/**
 * An activity's price for a quote that only knows `price`, `participantCount`
 * and optional lines (the voucher preview). Per person with priced lines, the
 * lines carry the money; with group size pricing only, `price` becomes the
 * charged unit; anything else is unchanged.
 */
const quotedActivityPrice = (
  activity: IPricedFormActivity
): {
  price: number;
  participantCount: number;
  ageGroups?: IBookingAgeGroupLine[];
} => {
  const price = toNumber(activity.price) ?? 0;
  const participantCount = countOf(activity.participantCount);
  if (!hasTieredPricing(activity)) return { price, participantCount };

  const perPerson = isChargedPerPerson(activity.priceVariant ?? 'per person');
  const priced = linesWithPeople(activity.ageGroups).filter(
    line => toNumber(line.unitPrice) !== undefined && !!line.modifierId
  );
  if (perPerson && priced.length > 0) {
    return {
      price,
      participantCount: priced.reduce(
        (sum, line) => sum + countOf(line.participantCount),
        0
      ),
      ageGroups: priced.map(line => ({
        modifierId: line.modifierId ?? '',
        participantCount: countOf(line.participantCount),
        unitPrice: toNumber(line.unitPrice) ?? 0,
      })),
    };
  }

  const total = storedActivityTotal(activity);
  return {
    price: perPerson
      ? roundMoney(total / Math.max(1, participantCount))
      : total,
    participantCount,
  };
};

export {
  reconcileAgeGroupLines,
  quotedActivityPrice,
  ageGroupSummary,
  activityAmount,
  linesWithPeople,
  hasTieredPricing,
  activityPricingSignature,
  alignAgeGroupLines,
};
export type { IPricedFormActivity };

export {
  defaultActivityTotal,
  differsFromDefault,
  roundMoney,
  mergeAgeGroupCounts,
  sumAgeGroupCounts,
  priceActivity,
  toAgeGroupPayload,
  storedActivityTotal,
  sortStoredAgeGroups,
};
export type {
  IProductAgeGroup,
  IBookingAgeGroupLine,
  IAgeGroupCount,
  IActivityPricing,
  IStoredAgeGroupLine,
};
